/**
 * Attachments API Layer.
 *
 * React Query hooks for file upload, listing, and deletion.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import type { Attachment } from "@/types/form";

export function useAttachments(
  entityType: string,
  entityId: string | undefined,
) {
  return useQuery<Attachment[]>({
    queryKey: ["attachments", entityType, entityId],
    queryFn: async () =>
      (
        await apiClient.get(
          `/api/v1/attachments?entity_type=${entityType}&entity_id=${entityId}`,
        )
      ).data as Attachment[],
    enabled: !!entityId,
  });
}

export function useUploadAttachment() {
  const qc = useQueryClient();
  return useMutation<
    Attachment,
    Error,
    { entityType: string; entityId: string; file: File }
  >({
    mutationFn: async ({ entityType, entityId, file }) => {
      const formData = new FormData();
      formData.append("entity_type", entityType);
      formData.append("entity_id", entityId);
      formData.append("file", file);
      return (
        await apiClient.post("/api/v1/attachments", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
      ).data as Attachment;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({
        queryKey: ["attachments", variables.entityType, variables.entityId],
      });
    },
  });
}

export function useDeleteAttachment() {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    { id: string; entityType: string; entityId: string }
  >({
    mutationFn: async ({ id }) => {
      await apiClient.delete(`/api/v1/attachments/${id}`);
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({
        queryKey: ["attachments", variables.entityType, variables.entityId],
      });
    },
  });
}
